import React, { useEffect, useRef } from "react";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import { LoadingPanel } from "../../components/layout/Loading";
import moment from "moment";
import ExpandableText from "../../components/common/ExpandableText";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import ShadowCard from "../../components/common/ShadowCard";
import { codeValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { Typography } from "@progress/kendo-react-common";
import { FaArrowLeft } from "react-icons/fa6";
import { Link, useNavigate } from "react-router-dom";
import FormTextField from "../../components/formFields/FormTextField";
import { giftCodeLedger } from "./services/giftcode.services";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import AlertBox from "../../components/common/AlertBox";
import { formatIndianNumber } from "../../_helper/helper";
import { clearGiftCodeLedgerDetails } from "./giftcodeSlice";

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const GiftCodeLedger: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const loading = useAppSelector((state) => state.giftcode.loading);
  const GiftCodeLedgerDetails = useAppSelector(
    (state) => state.giftcode.GiftCodeLedgerDetails
  );

  const LedgerList = GiftCodeLedgerDetails?.GiftCodeLedgerDetail || [];

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const gridRef = useRef<any>(null);

  useEffect(() => {
    return () => {
      dispatch(clearGiftCodeLedgerDetails());
    };
  }, []);

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? LedgerList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const handleSubmit = async (value: any) => {
    dispatch(giftCodeLedger(value?.GiftCode));
  };

  return (
    <GridLayout
      style={{ marginRight: 10 }}
      gap={{ rows: 0, cols: 10 }}
      cols={[{ width: "50%" }, { width: "50%" }]}
    >
      {loading && <LoadingPanel gridRef={gridRef} />}
      <GridLayoutItem className="d-flex align-items-center mb-2" colSpan={2}>
        <button
          onClick={() => navigate(-1)}
          className="btn btn-link me-3"
          style={{
            color: "#00a34d",
            background: "white",
            padding: "4px 6px 6px",
          }}
        >
          <FaArrowLeft size={20} />
        </button>
        <Typography.h4 style={{ margin: 0 }}>Gift Code Ledger</Typography.h4>
      </GridLayoutItem>
      <GridLayoutItem colSpan={2}>
        <Form
          onSubmit={handleSubmit}
          initialValues={{ GiftCode: "" }}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard
                style={{ padding: 20, paddingTop: 0, marginBottom: 20 }}
              >
                <GridLayout
                  style={{ marginRight: 10 }}
                  gap={{ rows: 0, cols: 10 }}
                  cols={[{ width: "50%" }, { width: "50%" }]}
                >
                  <GridLayoutItem colSpan={1}>
                    <Field
                      name="GiftCode"
                      label="Gift Code"
                      component={FormTextField}
                      placeholder="Gift Code"
                      astrike={true}
                      validator={codeValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={1}
                    style={{
                      textAlign: "start",
                      marginTop: "36px",
                      marginRight: 10,
                      width: "100%",
                    }}
                  >
                    <ButtonWithLoading
                      label="view"
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                  </GridLayoutItem>
                  {GiftCodeLedgerDetails?.MobileNo && (
                    <>
                      <GridLayoutItem
                        colSpan={2}
                        style={{
                          borderBottom: "2px dashed gray",
                          width: "100%",
                          margin: 0,
                          paddingBottom: 14,
                        }}
                      />
                      <GridLayoutItem colSpan={2}>
                        <h4
                          style={{
                            marginBottom: "12px",
                            textDecoration: "underline",
                            fontWeight: 600,
                            color: "#333",
                            fontSize: "18px",
                            marginTop: 15,
                          }}
                        >
                          Gift Code Details
                        </h4>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            columnGap: "24px",
                            rowGap: "12px",
                          }}
                        >
                          <div>
                            <p
                              style={{
                                margin: 0,
                                fontSize: "13px",
                                color: "#666",
                              }}
                            >
                              Validity
                            </p>
                            <p
                              style={{
                                margin: 0,
                                fontWeight: 600,
                                color: "#222",
                              }}
                            >
                              {GiftCodeLedgerDetails?.FromDate} -{" "}
                              {GiftCodeLedgerDetails?.ToDate}
                            </p>
                          </div>

                          <div>
                            <p
                              style={{
                                margin: 0,
                                fontSize: "13px",
                                color: "#666",
                              }}
                            >
                              Assign To
                            </p>
                            <p
                              style={{
                                margin: 0,
                                fontWeight: 600,
                                color: "#222",
                              }}
                            >
                              {GiftCodeLedgerDetails?.AssignUser}
                            </p>
                          </div>

                          <div>
                            <p
                              style={{
                                margin: 0,
                                fontSize: "13px",
                                color: "#666",
                              }}
                            >
                              Mobile
                            </p>
                            <p
                              style={{
                                margin: 0,
                                fontWeight: 600,
                                color: "#222",
                              }}
                            >
                              {GiftCodeLedgerDetails?.MobileNo}
                            </p>
                          </div>

                          <div>
                            <p
                              style={{
                                margin: 0,
                                fontSize: "13px",
                                color: "#666",
                              }}
                            >
                              Usage Count
                            </p>
                            <p
                              style={{
                                margin: 0,
                                fontWeight: 600,
                                color: "#222",
                              }}
                            >
                              {GiftCodeLedgerDetails?.UsageCount}
                            </p>
                          </div>

                          <div>
                            <p
                              style={{
                                margin: 0,
                                fontSize: "13px",
                                color: "#666",
                              }}
                            >
                              Min Amount Usage
                            </p>
                            <p
                              style={{
                                margin: 0,
                                fontWeight: 600,
                                color: "#222",
                              }}
                            >
                              ₹{" "}
                              {formatIndianNumber(
                                GiftCodeLedgerDetails?.MinAmtUsage || 0
                              )}
                            </p>
                          </div>

                          <div>
                            <p
                              style={{
                                margin: 0,
                                fontSize: "13px",
                                color: "#666",
                              }}
                            >
                              Max Amount Usage
                            </p>
                            <p
                              style={{
                                margin: 0,
                                fontWeight: 600,
                                color: "#222",
                              }}
                            >
                              ₹{" "}
                              {formatIndianNumber(
                                GiftCodeLedgerDetails?.MaxAmtUsage || 0
                              )}
                            </p>
                          </div>

                          <div>
                            <p
                              style={{
                                margin: 0,
                                fontSize: "13px",
                                color: "#666",
                              }}
                            >
                              Flight Ticket Amount
                            </p>
                            <p
                              style={{
                                margin: 0,
                                fontWeight: 600,
                                color: "#222",
                              }}
                            >
                              ₹{" "}
                              {formatIndianNumber(
                                GiftCodeLedgerDetails?.FlightTicketAmt || 0
                              )}
                            </p>
                          </div>

                          <div>
                            <p
                              style={{
                                margin: 0,
                                fontSize: "13px",
                                color: "#666",
                              }}
                            >
                              Hotel / Tour Amount
                            </p>
                            <p
                              style={{
                                margin: 0,
                                fontWeight: 600,
                                color: "#222",
                              }}
                            >
                              ₹{" "}
                              {formatIndianNumber(
                                GiftCodeLedgerDetails?.HotelBookingAmt || 0
                              )}
                            </p>
                          </div>

                          <div>
                            <p
                              style={{
                                margin: 0,
                                fontSize: "13px",
                                color: "#666",
                              }}
                            >
                              Pending Amount
                            </p>
                            <p
                              style={{
                                margin: 0,
                                fontWeight: 600,
                                color: "crimson",
                              }}
                            >
                              ₹{" "}
                              {formatIndianNumber(
                                GiftCodeLedgerDetails?.PendingAmt || 0
                              )}
                            </p>
                          </div>
                        </div>
                      </GridLayoutItem>
                    </>
                  )}
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </GridLayoutItem>
      {GiftCodeLedgerDetails?.MobileNo && (
        <GridLayoutItem colSpan={2}>
          {LedgerList && LedgerList.length ? (
            <KendoGrid
              style={{ maxHeight: "calc(100vh - 152px)" }}
              filterable={true}
              filter={filter}
              data={
                filter
                  ? filterBy(LedgerList, filter).slice(
                      page.skip,
                      page.take + page.skip
                    )
                  : LedgerList.slice(page.skip, page.take + page.skip)
              }
              onFilterChange={handleFilterChange}
              skip={page.skip}
              take={page.take}
              total={LedgerList.length}
              pageable={{
                buttonCount: 5,
                pageSizes: [5, 10, 15, "All"],
                pageSizeValue: pageSizeValue,
                type: "input",
              }}
              onPageChange={pageChange}
              cells={{
                headerCell: HeaderCustomCell,
                data: MyDataCustomCell,
              }}
            >
              <Column
                field="CreatedOn"
                title="Date Time"
                cells={{
                  data: (props: any) => {
                    return (
                      <td>
                        {props?.dataItem?.CreatedOn
                          ? moment(
                              props?.dataItem?.CreatedOn,
                              "DD-MM-YYYY HH:mm"
                            ).format("DD MMM YYYY hh:mm A")
                          : "-"}
                      </td>
                    );
                  },
                }}
              />
              <Column
                field="BookingNo"
                title="Booking No"
                width={130}
                cells={{
                  data: (props: any) => (
                    <td
                      style={{
                        ...props.style,
                      }}
                      className={`hoverableName ${props.className} text-center`}
                    >
                      <Link
                        className="hoverableName"
                        to={`/flightbooking/view/${props.dataItem?.BookingNo}`}
                        type="button"
                        style={{ color: "green", fontWeight: "bold" }}
                      >
                        {props.dataItem?.BookingNo}
                      </Link>
                    </td>
                  ),
                }}
              />
              <Column
                field="UsedAmount"
                title="Used Amt"
                cells={{
                  data: (props: any) => (
                    <td
                      style={{
                        ...props.style,
                        textAlign: "end",
                        fontWeight: "bold",
                      }}
                    >
                      ₹ {formatIndianNumber(props?.dataItem?.UsedAmount || 0)}
                    </td>
                  ),
                }}
                width={120}
              />
              <Column
                field="BookingAmt"
                title="Booking Amt"
                cells={{
                  data: (props: any) => (
                    <td
                      style={{
                        ...props.style,
                        textAlign: "end",
                        fontWeight: "bold",
                      }}
                    >
                      ₹ {formatIndianNumber(props?.dataItem?.BookingAmt || 0)}
                    </td>
                  ),
                }}
                width={120}
              />
              <Column field="UsedBy" title="Used By" />
              <Column
                field="Remarks"
                title="Remarks"
                width={300}
                cells={{
                  data: (props) =>
                    props.dataItem.Remarks ? (
                      <ExpandableText text={props.dataItem.Remarks} />
                    ) : (
                      <td></td>
                    ),
                }}
              />
            </KendoGrid>
          ) : (
            <AlertBox />
          )}
        </GridLayoutItem>
      )}
    </GridLayout>
  );
};

export default GiftCodeLedger;
